import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Camera, X } from "lucide-react";

export default function SitePhotos() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [siteMap, setSiteMap] = useState({});
  const [filter, setFilter] = useState({ client_id: "" });
  const [preview, setPreview] = useState(null);

  const load = () => {
    const q = filter.client_id ? `?client_id=${filter.client_id}` : "";
    api.get(`/photos${q}`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    load();  
  }, [filter.client_id]);

  useEffect(() => {
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {};
      r.data.forEach((c) => (m[c.id] = c.name));
      setClientMap(m);
    });
    api.get("/sites").then((r) => {
      const m = {};
      r.data.forEach((s) => (m[s.id] = s.name));
      setSiteMap(m);
    });
  }, []);

  // Flatten to individual photo tiles
  const tiles = items.flatMap((c) => {
    const arr = [];
    if (c.photo_before) arr.push({ ...c, _kind: "before", _url: c.photo_before });
    if (c.photo_after)  arr.push({ ...c, _kind: "after",  _url: c.photo_after });
    return arr;
  });

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Site Photos"
        subtitle="Every timestamped before/after photo captured during collections. Full traceability for audits."
        actions={
          <select
            value={filter.client_id}
            onChange={(e) => setFilter({ client_id: e.target.value })}
            className="hbt-input max-w-[220px]"
            data-testid="photo-client-filter"
          >
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        }
      />
      <div className="px-8 lg:px-12 py-8">
        {tiles.length === 0 ? (
          <div className="hbt-card p-16 text-center text-neutral-500">
            <Camera className="w-6 h-6 mx-auto mb-3 opacity-40" />
            No site photos captured yet. They’ll appear here as drivers upload them during collections.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {tiles.map((t, i) => (
              <button
                key={`${t.id}-${t._kind}`}
                onClick={() => setPreview(t)}
                className="group relative aspect-square overflow-hidden rounded-md border text-left hover:shadow-lg transition"
                style={{ borderColor: "var(--border)" }}
                data-testid={`photo-tile-${i}`}
              >
                <img src={t._url} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-white" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.75), transparent)" }}>
                  <div className="text-[10px] uppercase tracking-widest opacity-70">{t._kind} · {t.waste_type}</div>
                  <div className="text-xs font-semibold truncate">{clientMap[t.client_id] || "—"}</div>
                  <div className="text-[10px] font-mono-num opacity-80">{t.collection_date}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white mb-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70">{preview._kind} · {preview.waste_type}</div>
                <div className="font-semibold">{clientMap[preview.client_id]}</div>
                <div className="text-xs opacity-70">{siteMap[preview.site_id] || "—"} · {preview.collection_date}</div>
              </div>
              <button onClick={() => setPreview(null)} className="text-white/80 hover:text-white"><X className="w-6 h-6" /></button>
            </div>
            <img src={preview._url} className="w-full max-h-[80vh] object-contain rounded" alt="" />
          </div>
        </div>
      )}
    </div>
  );
}
